export const API_BASE = import.meta.env.VITE_API_URL ?? '/v1';

// In desktop (Electron) mode VITE_API_URL is always set (cross-origin 127.0.0.1:3737).
// Cookies cannot be sent cross-origin without HTTPS, so the desktop path keeps using Bearer.
const IS_DESKTOP = !!import.meta.env.VITE_API_URL;

// Set by AuthProvider (which lives inside <Router>) so a 401 can navigate
// in-app via React Router instead of a raw `window.location` assignment.
// A raw assignment forces a hard reload — under BrowserRouter that risks a
// reload loop (see TRACKER.md R12), and under HashRouter/file:// (the
// packaged desktop app) a bare '/login' path doesn't resolve to the hash
// route at all, it tries to load a nonexistent file and fails outright.
type NavigateFn = (path: string) => void;
let authRedirectHandler: NavigateFn | null = null;
export function setAuthRedirectHandler(fn: NavigateFn | null): void {
  authRedirectHandler = fn;
}

// A page can fire several parallel requests (invoices list, dashboard summary,
// realtime-sync handshake, ...); if the session is bad, they all 401 around the
// same time. Collapse that into a single redirect instead of one navigate() per
// failed request.
let redirecting = false;

function redirectToLogin(): void {
  if (redirecting) return;
  redirecting = true;
  setTimeout(() => { redirecting = false; }, 1000);

  if (authRedirectHandler) {
    authRedirectHandler('/login');
    return;
  }
  // Fallback for the rare case a 401 fires before AuthProvider has mounted.
  // Still guard against reassigning the same path (see R12).
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly fieldErrors?: Array<{ field: string; errors: string[] }>,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const token = IS_DESKTOP ? sessionStorage.getItem('cl_token') : null;
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: IS_DESKTOP ? 'same-origin' : 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });

  if (res.status === 401) {
    if (IS_DESKTOP) {
      sessionStorage.removeItem('cl_token');
      sessionStorage.removeItem('cl_user');
    }
    redirectToLogin();
    throw new ApiError(401, 'Session expired');
  }

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    const body = (errText ? JSON.parse(errText) : {}) as { message?: string; errors?: Array<{ field: string; errors: string[] }>; code?: string };
    throw new ApiError(
      res.status,
      body.message ?? `Request failed (${res.status})`,
      body.errors,
      body.code,
    );
  }

  if (res.status === 204) return null as T;
  const text = await res.text();
  if (!text) return null as T;
  return JSON.parse(text) as T;
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) => apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
